// src/ui/overlays/UpdateToast.tsx — PWA-04 update-available toast.
//
// vite-plugin-pwa with `registerType: 'autoUpdate'` handles skipWaiting +
// clientsClaim automatically (D-20). The `useRegisterSW` hook exposes:
//   - needRefresh: true when a new SW is waiting to activate
//   - updateServiceWorker(reloadPage: true) → calls skipWaiting and reloads
//
// The toast has no auto-dismiss (per UI-SPEC §Update Available Toast) — it
// stays until the user taps. No "later" button either: ignoring it keeps the
// old version running until the next navigation, which is the graceful
// fallback autoUpdate already provides.
import { type JSX } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

import './overlays.css';

export function UpdateToast(): JSX.Element | null {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      // Re-check for a newer SW when the tab regains focus. Family members
      // keeping the game as a backgrounded home-screen app otherwise only
      // learn about updates on navigation; tapping back in on iPad shouldn't
      // leave them stranded for a whole day.
      if (registration) {
        window.addEventListener('focus', () => {
          void registration.update();
        });
      }
    },
  });

  if (!needRefresh) return null;

  return (
    <div
      className="update-toast"
      data-testid="update-toast"
      role="alert"
      onClick={() => {
        void updateServiceWorker(true);
      }}
    >
      <span className="update-toast__title">New version available</span>
      <span className="update-toast__cta">Tap to update</span>
    </div>
  );
}
